import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";
import flowbuilder from "../../assets/flowbuilders.png";

import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage";
import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  integrationCell: {
    minWidth: 240,
  },
  integrationContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 96,
    height: 32,
    borderRadius: 6,
    flexShrink: 0,
  },
  integrationMeta: {
    minWidth: 0,
  },
  integrationName: {
    fontWeight: 600,
  },
  integrationSubtext: {
    opacity: 0.72,
  },
  limitsStack: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_INTEGRATIONS": {
      const queueIntegration = action.payload;
      const newIntegrations = [];

      queueIntegration.forEach((integration) => {
        const integrationIndex = state.findIndex((item) => item.id === integration.id);

        if (integrationIndex !== -1) {
          state[integrationIndex] = integration;
        } else {
          newIntegrations.push(integration);
        }
      });

      return [...state, ...newIntegrations];
    }
    case "UPDATE_INTEGRATIONS": {
      const queueIntegration = action.payload;
      const integrationIndex = state.findIndex((item) => item.id === queueIntegration.id);

      if (integrationIndex !== -1) {
        state[integrationIndex] = queueIntegration;
        return [...state];
      }

      return [queueIntegration, ...state];
    }
    case "DELETE_INTEGRATION": {
      const integrationId = action.payload;
      return state.filter((item) => item.id !== integrationId);
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

const integrationAssets = {
  dialogflow: { image: dialogflow, label: "Dialogflow" },
  n8n: { image: n8n, label: "N8N" },
  webhook: { image: webhooks, label: "Webhook" },
  typebot: { image: typebot, label: "Typebot" },
  flowbuilder: { image: flowbuilder, label: "Flowbuilder" },
};

const QueueIntegration = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingIntegration, setDeletingIntegration] = useState(null);
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [queueIntegration, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);

      if (!planConfigs.plan.useIntegrations) {
        toast.error(
          "Esta empresa nao possui permissao para acessar essa pagina. Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push("/");
        }, 1000);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        setLoading(true);

        try {
          if (pageNumber === 1) {
            dispatch({ type: "RESET" });
          }

          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });

          dispatch({
            type: "LOAD_INTEGRATIONS",
            payload: data.queueIntegrations || [],
          });
          setHasMore(Boolean(data.hasMore));
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchIntegrations();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, searchParam]);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };

    socket.on(`company-${companyId}-queueIntegration`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueEvent);
    };
  }, [companyId, socket]);

  const handleOpenIntegrationModal = () => {
    setSelectedIntegration(null);
    setIntegrationModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setIntegrationModalOpen(false);
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    dispatch({ type: "RESET" });
    setSearchParam(newSearchParam);
    setPageNumber(1);
  };

  const handleEditIntegration = (integration) => {
    setSelectedIntegration(integration);
    setIntegrationModalOpen(true);
  };

  const handleDeleteIntegration = async () => {
    if (!deletingIntegration) return;

    try {
      await api.delete(`/queueIntegration/${deletingIntegration.id}`);
      toast.success("Integracao excluida com sucesso");
      dispatch({ type: "DELETE_INTEGRATION", payload: deletingIntegration.id });
    } catch (err) {
      toastError(err);
    }

    setDeletingIntegration(null);
    setConfirmModalOpen(false);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const refreshIntegrations = () => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  };

  const summary = useMemo(() => {
    const dialogflowCount = queueIntegration.filter(
      (integration) => integration.type === "dialogflow"
    ).length;
    const automationCount = queueIntegration.filter((integration) =>
      ["n8n", "webhook", "typebot", "flowbuilder"].includes(integration.type)
    ).length;
    const withLanguage = queueIntegration.filter((integration) =>
      Boolean(integration.language)
    ).length;

    return {
      total: queueIntegration.length,
      dialogflowCount,
      automationCount,
      withLanguage,
      visible: queueIntegration.length,
    };
  }, [queueIntegration]);

  const summaryCards = useMemo(
    () => [
      { key: "integrations", label: "Integracoes", value: summary.total },
      { key: "dialogflow", label: "Dialogflow", value: summary.dialogflowCount },
      { key: "automations", label: "Automacoes", value: summary.automationCount },
      { key: "language", label: "Com idioma", value: summary.withLanguage },
    ],
    [summary]
  );

  if (user.profile === "user") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingIntegration
            ? `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingIntegration.name}?`
            : i18n.t("queueIntegration.confirmationModal.deleteTitle")
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingIntegration(null);
        }}
        onConfirm={handleDeleteIntegration}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <IntegrationModal
        open={integrationModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration?.id}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("queueIntegration.title")}
            </Typography>
          </Box>
          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshIntegrations} disabled={loading}>
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenIntegrationModal}
            >
              {i18n.t("queueIntegration.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de integracoes" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Integracoes operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("queueIntegration.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar integracoes" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.visible} visiveis`} />
              <Chip size="small" label={`${summary.automationCount} automacoes`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queueIntegration.table.id")}
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    {i18n.t("queueIntegration.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queueIntegration.table.type")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    Detalhes
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("queueIntegration.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queueIntegration.map((integration) => {
                  const integrationMeta =
                    integrationAssets[integration.type] || integrationAssets.webhook;

                  return (
                    <TableRow key={integration.id} hover>
                      <TableCell align="center">{integration.id}</TableCell>
                      <TableCell className={classes.integrationCell}>
                        <Box className={classes.integrationContent}>
                          <Avatar src={integrationMeta.image} className={classes.avatar} />
                          <Box className={classes.integrationMeta}>
                            <Typography
                              variant="body2"
                              className={classes.integrationName}
                            >
                              {integration.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              className={classes.integrationSubtext}
                              noWrap
                            >
                              {integration.projectName || integrationMeta.label}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" variant="outlined" label={integrationMeta.label} />
                      </TableCell>
                      <TableCell align="center">
                        <Box className={classes.limitsStack}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              integration.language
                                ? integration.language.toUpperCase()
                                : "Sem idioma"
                            }
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              integration.updatedAt
                                ? format(parseISO(integration.updatedAt), "dd/MM/yy HH:mm")
                                : "--"
                            }
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center" className={classes.actionsCell}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditIntegration(integration)}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingIntegration(integration);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && queueIntegration.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhuma integracao encontrada</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre uma nova integracao para continuar.
              </Typography>
            </Box>
          )}

          {!loading && hasMore && queueIntegration.length > 0 && (
            <div className={classes.loadMoreBox}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais integracoes
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default QueueIntegration;
